import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  Inbox, Users, Package, Watch, Gem, Sparkles, Newspaper, HelpCircle,
  Settings as SettingsIcon, LogOut, Menu, X, Store, Sun, Moon, Languages, Star, MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { useAdminBadges } from "@/context/AdminBadgeContext";
import NotificationBell from "@/components/admin/NotificationBell";

const GROUPS = (es) => [
  {
    label: es ? "Gestión de Clientes" : "Client Management",
    items: [
      { to: "/admin/requests", label: es ? "Solicitudes" : "Requests", icon: Inbox },
      { to: "/admin/clients", label: es ? "Clientes" : "Clients", icon: Users },
    ],
  },
  {
    label: es ? "Inventario" : "Inventory",
    items: [
      { to: "/admin/inventory/men", label: es ? "Hombre" : "Men", icon: Package },
      { to: "/admin/inventory/women", label: es ? "Mujer" : "Women", icon: Gem },
      { to: "/admin/inventory/accessories", label: es ? "Accesorios" : "Accessories", icon: Sparkles },
      { to: "/admin/inventory/watches", label: es ? "Relojes" : "Watches", icon: Watch },
    ],
  },
  {
    label: es ? "Contenido web" : "Web Content",
    items: [
      { to: "/admin/content/blog", label: "Blog", icon: Newspaper },
      { to: "/admin/content/faqs", label: "FAQs", icon: HelpCircle },
      { to: "/admin/reviews", label: es ? "Reseñas" : "Reviews", icon: Star },
      { to: "/admin/messages", label: es ? "Mensajes" : "Messages", icon: MessageSquare },
    ],
  },
  {
    label: es ? "Configuración" : "Settings",
    items: [
      { to: "/admin/settings", label: es ? "Configuración" : "Settings", icon: SettingsIcon },
    ],
  },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { lang, toggleLang } = useLanguage();
  const { unreadMessages } = useAdminBadges();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const es = lang === "es";

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6 border-b border-border">
        <Link to="/admin" className="font-serif font-bold text-2xl tracking-[0.12em] whitespace-nowrap">M&amp;A Jewelry</Link>
        <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1">{es ? "Panel de administración" : "Admin Console"}</p>
      </div>
      <nav className="flex-1 p-3 space-y-5 overflow-y-auto">
        {GROUPS(es).map((g) => (
          <div key={g.label}>
            <p className="px-4 mb-2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground/70">{g.label}</p>
            <div className="space-y-0.5">
              {g.items.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`admin-nav-${l.to.split("/").pop()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isActive ? "bg-gold/15 text-gold border-l-2 border-gold" : "text-muted-foreground hover:text-foreground hover:bg-secondary border-l-2 border-transparent"}`
                  }
                >
                  <l.icon className="h-4 w-4" /> <span className="flex-1">{l.label}</span>
                  {l.to === "/admin/messages" && unreadMessages > 0 && (
                    <span
                      className="ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-gold text-[10px] font-semibold text-black leading-none"
                      data-testid="messages-unread-badge"
                    >
                      {unreadMessages > 99 ? "99+" : unreadMessages}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-border space-y-1">
        <Link to="/" className="flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" data-testid="admin-view-store">
          <Store className="h-4 w-4" /> {es ? "Ver tienda" : "View Store"}
        </Link>
        <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground hover:text-destructive transition-colors" data-testid="admin-logout">
          <LogOut className="h-4 w-4" /> {es ? "Salir" : "Sign Out"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background" data-testid="admin-layout">
      <aside className="hidden lg:block w-64 border-r border-border shrink-0 h-screen sticky top-0">
        <Sidebar />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative w-64 bg-background border-r border-border h-full">
            <button className="absolute top-5 right-4 z-10" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            <Sidebar />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <header className="h-16 border-b border-border flex items-center justify-between px-5 lg:px-8 sticky top-0 glass z-40">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-btn"><Menu className="h-5 w-5" /></Button>
            <span className="text-sm text-muted-foreground truncate max-w-[220px]" data-testid="admin-user-email">{user?.email}</span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Button variant="ghost" size="sm" className="px-2 text-xs font-medium tracking-widest" onClick={toggleLang} data-testid="admin-lang-toggle" aria-label="Toggle language">
              <Languages className="h-4 w-4 mr-1" /> {lang === "en" ? "ES" : "EN"}
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="admin-theme-toggle">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </header>
        <main className="p-5 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
